import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import { RelatedSalon } from '../../../../modules/provider/Profile';
import { Stack } from '../../../ui';
import { PublicProvider } from '../../../../api/Providers';
import { RelatedNearbyPros } from './RelatedNearbyPros';
import { FindSomeoneNewSection } from './FindSomeoneNewSection';
import { InactiveProMessage } from './InactiveProMessage';
import {
  useIsMobile,
  useOnce,
  useResponsiveEffect,
} from '../../../../hooks';
import { ICrossPromotedProvider } from '../CrossPromotion';
import useSearchTiles from './useSearchTiles';
import responsiveConfig from '../../../../modules/responsive_config';
import { AUTO_SUGGEST_Z_INDEX } from './constants';
import { LocationRecord } from '../../../../store/Location.model';
import type { IRootDispatch, RootState } from '../../../../store/models';
import { SEARCH_PARAM_RESET_CODE } from '../../../../modules/consumer/search/Constants';
import { toSlugCase } from '../../../../modules/stringUtils';
import analytics from '../../../../modules/analytics';
import { SEARCH_ROUTE } from '../../../../route-names';

type InactiveProfileProps = {
  profile: Pick<PublicProvider, 'id' | 'is_disabled' | 'name' | 'profile_photo'>;
  proSuggestions: RelatedSalon[];
};

export const InactiveProfile = ({
  profile,
  proSuggestions,
}: InactiveProfileProps) => {
  const isMobile = useIsMobile();
  const is = useResponsiveEffect();
  const suggestions = proSuggestions.slice(0, isMobile ? 3 : 3);
  const width = (
    is.desktopNarrow
      ? responsiveConfig.tablet.max
      : responsiveConfig['desktop-narrow'].max
  );
  const dispatch = useDispatch<IRootDispatch>();
  const searchLocation: LocationRecord = useSelector<RootState, LocationRecord>(
    state => state.search,
  );
  const deviceLocation: LocationRecord = useSelector<RootState, LocationRecord>(
    state => state.location,
  );
  const [location, setLocation] = useState<LocationRecord | null>(
    searchLocation.address ? searchLocation : deviceLocation,
  );
  const [queryText, setQueryText] = useState<string>('');
  const searchParams = useSelector<RootState, any>(state => state.search);
  const inactiveSearchParams = useMemo(() => ({
    ...searchParams,
    loc: toSlugCase(location?.address),
    lat: location?.latitude ?? SEARCH_PARAM_RESET_CODE,
    lon: location?.longitude ?? SEARCH_PARAM_RESET_CODE,
    placeId: location?.placeId ?? SEARCH_PARAM_RESET_CODE,
    q: queryText,
    source: 'inactive_profile',
  }), [
    searchParams,
    queryText,
    location,
  ]);

  const { handleTileClick, tiles } = useSearchTiles();

  const handleInactiveSearchSubmit = (query: string): void => {
    if (location?.address) {
      dispatch.route.go({
        route: SEARCH_ROUTE,
        params: {
          ...inactiveSearchParams,
          q: query,
        },
        notify: true,
      });
    } else {
      setQueryText(query);
    }
  };

  const handleOnSuggestedProPress = (provider: ICrossPromotedProvider) => {
    analytics.track('client_inactiveprofile_relatedsalon_click', {
      destination_provider_id: provider.provider_id,
    });
  };

  useOnce(() => {
    analytics.track('client_inactive_proprofile_viewed', {
      provider_id: profile.id,
    });
  }, profile?.is_disabled || null);

  return (
    <Inline justifyContent="center" paddingBottom={40}>
      <Stack
        testID="provider-inactive-profile"
        color={colors.whiteWash}
        paddingBottom={isMobile ? 0 : 84}
        minHeight="100%"
        style={{ width: isMobile ? '100vw' : width }}
      >
        <Box color={colors.superLight}>
          <InactiveProMessage profile={profile} />
        </Box>

        <Stack
          paddingBottom={isMobile ? 4 : 56}
          style={{ zIndex: AUTO_SUGGEST_Z_INDEX }}
        >
          <FindSomeoneNewSection
            onSubmit={handleInactiveSearchSubmit}
            searchParams={inactiveSearchParams}
            tiles={tiles}
            location={location}
            onTileClick={handleTileClick}
            onUpdateLocation={setLocation}
            onUpdateQuery={setQueryText}
          />
          {suggestions.length > 0 && (
            <RelatedNearbyPros
              proSuggestions={suggestions}
              onSelectPro={handleOnSuggestedProPress}
            />
          )}
        </Stack>
      </Stack>
    </Inline>
  );
};
